import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

class CartDrawerComponent extends Component {
  #summaryThreshold = 0.5;

  get #themeDrawer() {
    return this.closest('theme-drawer');
  }

  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')
        ?.shadowRoot?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, {
            once: true
          });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}

// Fechamento automático após 15 segundos de inatividade.
(() => {
  const INACTIVITY_TIME = 15000;

  if (window.novaEraCartAutoCloseInstalled) return;
  window.novaEraCartAutoCloseInstalled = true;

  const timers = new WeakMap();

  function clearTimer(drawer) {
    window.clearTimeout(timers.get(drawer));
    timers.delete(drawer);
  }

  function restartTimer(drawer) {
    clearTimer(drawer);

    if (!drawer.hasAttribute('open')) return;

    const timer = window.setTimeout(() => {
      if (!drawer.isConnected || !drawer.hasAttribute('open')) {
        return;
      }

      const activeElement = document.activeElement;
      const editingField =
        activeElement instanceof Element &&
        drawer.contains(activeElement) &&
        activeElement.matches(
          'input, textarea, select, [contenteditable="true"]'
        );

      const anotherDialogOpen = Array.from(
        document.querySelectorAll('dialog[open]')
      ).some((dialog) => !drawer.contains(dialog));

      if (editingField || anotherDialogOpen || document.hidden) {
        restartTimer(drawer);
        return;
      }

      if (typeof drawer.close === 'function') {
        drawer.close();
      }
    }, INACTIVITY_TIME);

    timers.set(drawer, timer);
  }

  document.addEventListener('theme-drawer:open', (event) => {
    const drawer = event.target;

    if (drawer instanceof Element && drawer.id === 'cart-drawer') {
      restartTimer(drawer);
    }
  });

  document.addEventListener('theme-drawer:close', (event) => {
    const drawer = event.target;

    if (drawer instanceof Element && drawer.id === 'cart-drawer') {
      clearTimer(drawer);
    }
  });

  const interactionEvents = [
    'pointermove',
    'pointerdown',
    'keydown',
    'input',
    'change',
    'scroll',
    'focusin',
    'focusout'
  ];

  interactionEvents.forEach((eventName) => {
    document.addEventListener(
      eventName,
      (event) => {
        if (!(event.target instanceof Element)) return;

        const drawer = event.target.closest('#cart-drawer');

        if (drawer) restartTimer(drawer);
      },
      { capture: true, passive: true }
    );
  });

  document.addEventListener('visibilitychange', () => {
    const drawer = document.getElementById('cart-drawer');

    if (drawer) restartTimer(drawer);
  });

  const drawer = document.getElementById('cart-drawer');

  if (drawer) restartTimer(drawer);
})();
